import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaRegEdit, FaTrashAlt } from 'react-icons/fa';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AssetManagerPage from 'components/common/AssetManagerPage';
import { useConfirm } from 'contexts';
import Layout from 'components/common/Layout';
import { useDeleteProduct, useToast, useUpdateProduct, useFetchProduct, useUploadDesignFile } from 'hooks';
import ProductFieldGroup from 'components/common/ProductFieldGroup';
import ProductFieldWrapper from 'components/common/ProductFieldWrapper';
import ButtonProc from 'components/common/Buttons/ButtonProc';
import ProductField from 'components/productManager/ProductField';
import ProductSection from 'components/productManager/ProductSection';
import UpdateThumbnail from 'components/productManager/UpdateThumbnail';
import UpdateVersion from 'components/productManager/UpdateVersion';
import UpdateZipFile from 'components/productManager/UpdateZipFile';
import { IconButton } from 'components/UI';
import { Pages } from 'constants/pages';
import AppRoutes from 'constants/routes';
import { checkIfError } from 'utils/helpers';
import { productModel } from 'constants/productModel';
import ParameterGroup from 'components/Configurator/Menus/ParameterGroup';
import { IParameter, IProductResource } from '../../../server/types';
import ProductParameterItem from './ProductParameterItem';

const Product = () => {
  const params = useParams();
  const location = useLocation();

  const path = location.pathname;

  const [
    uploadDesignFile,
    { loading: uploadDesignFileLoading, progress: uploadDesignFileProgress, error: uploadDesignFileError },
  ] = useUploadDesignFile();
  const [
    uploadQuoteFile,
    { loading: uploadQuoteFileLoading, progress: uploadQuoteFileProgress, error: uploadQuoteFileError },
  ] = useUploadDesignFile();
  const [fetchProduct] = useFetchProduct();
  const [deleteProduct] = useDeleteProduct();
  const [updateProduct, { loading: isUpdatingProduct, error: updateProductError }] = useUpdateProduct();

  const [designZipFile, setDesignZipFile] = useState<File | null>(null);
  const [quoteZipFile, setQuoteZipFile] = useState<File | null>(null);

  const [showConfirm] = useConfirm();
  const [showToast] = useToast();

  const navigate = useNavigate();

  const [initialState, setUpdatedState] = useState({
    loading: false,
    productData: {} as IProductResource | null,
    productModel: {},
    name: params.productId,
    showUpdateThumbnail: false,
    showUpdateReference: false,
    showUpdateVersion: false,
    showUpdateFile: false,
    showUpdateQuote: false,
    showDeleteConfirm: false,
    path,
    version: '' as `Autodesk.Inventor+${26 | 27 | 28 | 29 | 30}` | null,
  });

  const updateValues = async (productData?: IProductResource) => {
    setUpdatedState({ ...initialState, loading: true });

    if (productData) {
      setUpdatedState({
        ...initialState,
        productData,
        productModel,
        loading: false,
        showUpdateThumbnail: false,
        showUpdateReference: false,
        showUpdateVersion: false,
        showUpdateFile: false,
        showUpdateQuote: false,
        version: productData.version,
      });
    } else {
      setUpdatedState({
        ...initialState,
        productData: null as unknown as IProductResource,
        productModel: {},
        loading: false,
        version: null,
      });
    }

    return productData?.id;
  };

  const handleInitialLoad = async () => {
    const productData = await fetchProduct(params.productId as string);
    updateValues(productData as IProductResource);
  };

  useEffect(() => {
    handleInitialLoad();
  }, []);

  useEffect(() => {
    if (updateProductError) {
      showToast({
        status: 'error',
        message: `Deletion failed: ${updateProductError?.data?.message ?? 'unknown error'}`,
      });
    }
  }, [updateProductError]);

  const updateValue = (name: string, value: string) =>
    setUpdatedState((prevInitialState) => ({
      ...prevInitialState,
      [name]: value,
    }));

  const confirmProductDeleteClick = async () => {
    await deleteProduct(initialState.productData?.id!);
    const route = AppRoutes.ProductManager;
    navigate(route);
  };

  const deleteProductClick = () => {
    setUpdatedState({ ...initialState, showDeleteConfirm: true });

    showConfirm({
      onConfirm: confirmProductDeleteClick,
      title: 'Delete product',
      message: 'Are you sure you want to delete this product?',
    });
  };

  const handleDesignFileUpload = async (file: File, bucketLocation: string | Blob, quote = false) => {
    const field = quote ? 'Quote' : 'Input file';
    const formData = new FormData();

    formData.append('bucket', bucketLocation);
    formData.append('fileSize', `${file.size}`);
    formData.append('file', file);

    const uploadFn = quote ? uploadQuoteFile : uploadDesignFile;

    const response = await uploadFn(formData);

    const updateFn = quote ? setQuoteZipFile : setDesignZipFile;

    updateFn(null);

    if (checkIfError(response)) {
      return;
    }

    showToast({
      status: 'success',
      message: `${field} uploaded successfully!`,
    });
  };

  const renderEditIcon = (fieldName: string) => {
    const fields = {
      Version: 'showUpdateVersion',
      'Reference Image Url': 'showUpdateReference',
      'Thumbnail Image Url': 'showUpdateThumbnail',
      'Input File Zip Name': 'showUpdateFile',
      Quote: 'showUpdateQuote',
    };

    if (!Object.prototype.hasOwnProperty.call(fields, fieldName)) return null;

    return (
      <IconButton
        aria-label={`Edit ${[fieldName]}`}
        title="Edit"
        mr={3}
        h={8}
        w={8}
        fontSize="sm"
        icon={<FaRegEdit />}
        onClick={() =>
          setUpdatedState({
            ...initialState,
            [fields[fieldName as keyof typeof fields]]: true,
          })
        }
      />
    );
  };

  const renderDeleteIcon = (displayName: string) => {
    const fields: {
      [displayName: string]: {
        name: string;
        productFieldName: keyof IProductResource;
        isLoading?: boolean;
      };
    } = {
      'Thumbnail Image Url': {
        name: 'Thumbnail',
        productFieldName: 'thumbnailImage',
        isLoading: isUpdatingProduct,
      },
      'Reference Image Url': {
        name: 'Reference',
        productFieldName: 'refImage',
        isLoading: isUpdatingProduct,
      },
    };

    const field = fields[displayName];
    if (!field || !initialState.productData?.[field.productFieldName]) {
      return null;
    }

    const handleDeleteImage = () => {
      showConfirm({
        onConfirm: async () => {
          const updatedProduct = await updateProduct(
            initialState.productData?.id as string,
            field.productFieldName === 'thumbnailImage' ? { thumbnailImage: '' } : { refImage: '' }
          );

          if (!updateProductError) {
            showToast({
              status: 'success',
              message: `${field.name} image deleted successfully!`,
            });
            await updateValues(updatedProduct as IProductResource);
          }
        },
        title: `Delete ${field.name}`,
        message: `Are you sure you want to delete this ${field.name} image?`,
      });
    };

    return (
      <Box>
        <IconButton
          aria-label={`Delete ${field.name}`}
          mr={3}
          h={8}
          w={8}
          fontSize="sm"
          icon={<FaTrashAlt />}
          isLoading={field.isLoading}
          isDisabled={!initialState.productData?.[field.productFieldName]}
          onClick={handleDeleteImage}
        />
      </Box>
    );
  };

  const renderUpdateThumbnail = () => {
    if (!initialState.showUpdateThumbnail) return null;
    return (
      <UpdateThumbnail
        onCancelUploadClick={(update) => {
          if (update) updateValues(update);
          setUpdatedState((prevInitialState) => ({
            ...prevInitialState,
            showUpdateThumbnail: false,
          }));
        }}
        productId={initialState.productData?.id as string}
        directory="thumbnail"
        displayName="Thumbnail Image"
      />
    );
  };

  const renderUpdateRefImage = () => {
    if (!initialState.showUpdateReference) return null;

    return (
      <UpdateThumbnail
        displayName="Reference image"
        onCancelUploadClick={(update) => {
          if (update) updateValues(update);
          setUpdatedState((prevInitialState) => ({
            ...prevInitialState,
            showUpdateReference: false,
          }));
        }}
        productId={initialState.productData?.id as string}
        directory="referenceImage"
      />
    );
  };

  const renderUpdateVersion = () => {
    if (!initialState.showUpdateVersion) return null;

    return (
      <UpdateVersion
        displayName="Version"
        onCancelUploadClick={(update) => {
          if (update) updateValues(update);
          setUpdatedState((prevInitialState) => ({
            ...prevInitialState,
            showUpdateVersion: false,
          }));
        }}
        onChange={updateValue}
        productId={initialState.productData?.id as string}
        data={initialState.version as `Autodesk.Inventor+${27 | 28 | 29 | 30}`}
      />
    );
  };

  const renderUpdateFile = () => {
    if (!initialState.showUpdateFile) return null;

    return (
      <UpdateZipFile
        fileLimit={5000}
        displayName="DESIGN FILE"
        title="UPDATE DESIGN FILE"
        zipFile={designZipFile}
        loading={uploadDesignFileLoading}
        progress={uploadDesignFileProgress}
        error={uploadDesignFileError}
        onChange={(file: File) => setDesignZipFile(file)}
        onUpload={handleDesignFileUpload}
        onCancelUploadClick={(update) => {
          if (update) updateValues(initialState.productData as IProductResource);
          setUpdatedState((prevInitialState) => ({
            ...prevInitialState,
            showUpdateFile: false,
          }));

          if (!uploadDesignFileLoading) {
            setDesignZipFile(null);
          }
          if (!uploadQuoteFileLoading) {
            setQuoteZipFile(null);
          }
        }}
        data={initialState.productData?.zipFile?.file.objectName as string}
        bucket={initialState.productData?.zipFile?.file.bucket as string}
      />
    );
  };

  const renderQuoteFile = () => {
    if (!initialState.showUpdateQuote) return null;
    return (
      <UpdateZipFile
        fileLimit={100}
        displayName="QUOTE FILE"
        title="UPDATE QUOTE FILE"
        zipFile={quoteZipFile}
        loading={uploadQuoteFileLoading}
        progress={uploadQuoteFileProgress}
        error={uploadQuoteFileError}
        onChange={(file: File) => setQuoteZipFile(file)}
        onUpload={(zipFile: File, bucket: string) => handleDesignFileUpload(zipFile, bucket, true)}
        onCancelUploadClick={(update) => {
          if (update) updateValues(initialState.productData as IProductResource);
          setUpdatedState((prevInitialState) => ({
            ...prevInitialState,
            showUpdateQuote: false,
          }));
        }}
        data={initialState.productData?.quote?.objectName as string}
        bucket={initialState.productData?.quote?.bucket as string}
      />
    );
  };

  const renderFields = (
    model:
      | { [key: string]: { displayName: string; inputType: string } }
      | {
          displayName: string;
          inputType: string;
        },
    data: IProductResource
  ): React.ReactNode[] | null => {
    if (data === undefined) return null;

    return Object.entries(model)
      .filter(([key]) => key !== 'parameters' && key !== 'displayName')
      .map(([key, value]: [string, { displayName: string; inputType: string }]) => {
        const productData = data[key as keyof IProductResource] ?? undefined;

        if (key === 'preloaded') {
          return null;
        }

        if (key === 'zipFile') {
          return (
            <Box key={key}>
              <ProductFieldGroup>{value.displayName}</ProductFieldGroup>
              {renderFields(value, productData as any)}
            </Box>
          );
        }

        if (value.inputType !== 'fileSelect') {
          return (
            <HStack key={key} my={1} border="none" bgColor="base2Colour.500">
              <ProductField
                name={key}
                displayName={value.displayName}
                data={productData}
                fieldType={value.inputType}
                edit={false}
              />
              <Box>{renderEditIcon(value.displayName)}</Box>
              {renderDeleteIcon(value.displayName)}
            </HStack>
          );
        }

        if (value.inputType !== 'fileSelect') {
          return (
            <Box key={key} my={1}>
              <ProductField
                name={key}
                displayName={value.displayName}
                data={productData}
                fieldType={value.inputType}
                edit={false}
              />
            </Box>
          );
        }

        return (
          <ProductFieldWrapper key={key}>
            <ProductFieldGroup>{value.displayName}</ProductFieldGroup>
            <Box>
              <ProductFieldWrapper
                sx={{
                  justifyContent: 'flex-start',
                  bgColor: 'base2Colour.500',
                  my: 0,
                  px: 0,
                  py: 0,
                  alignItems: 'space-between',
                  fontSize: 'sm',
                  color: 'base2FontColour.500',
                }}
              >
                <HStack pl={4}>
                  <ProductField
                    name="objectName"
                    displayName="File"
                    data={(productData as { objectName: string }).objectName}
                    fieldType={value.inputType}
                    edit={false}
                    showSelect
                  />
                  <Box>{renderEditIcon(value.displayName)}</Box>
                </HStack>
              </ProductFieldWrapper>
              <ProductFieldWrapper
                sx={{
                  justifyContent: 'flex-start',
                  bgColor: 'base2Colour.500',
                  my: 0,
                  pr: 2,
                  pl: 4,
                  py: 0,
                  alignItems: 'space-between',
                  fontSize: 'sm',
                  color: 'base2FontColour.500',
                }}
              >
                <ProductField
                  name="bucket"
                  displayName="Bucket"
                  data={(productData as { bucket: string }).bucket}
                  fieldType={value.inputType}
                  edit={false}
                />
              </ProductFieldWrapper>
            </Box>
          </ProductFieldWrapper>
        );
      });
  };

  const renderFieldGroups = (
    model:
      | { [key: string]: { displayName: string; inputType: string } }
      | {
          displayName: string;
          inputType: string;
        },
    data: IProductResource
  ) => {
    if (data === undefined) return null;

    return renderFields(model, data);
  };

  const renderParameters = (data: IParameter[]) => {
    const paramKeys = initialState.productData?.parameterList;

    if (paramKeys === undefined) return null;

    const renderParameterGroups = paramKeys.map(({ title, pList }) => (
      <ParameterGroup
        key={title}
        buttonProps={{
          bg: 'base2Colour.500',
          color: 'base2FontColour.500',
          _hover: {
            bgColor: 'base2Colour.400',
          },
          px: 2,
        }}
        panelProps={{
          p: 2,
        }}
        boxProps={{
          p: 2,
        }}
        paramKeys={paramKeys}
        title={title}
      >
        <ProductParameterItem parameters={pList} params={data} />
      </ParameterGroup>
    ));

    return <Box sx={{ mt: 0, p: 0 }}>{renderParameterGroups}</Box>;
  };

  return (
    <Layout pages={[Pages.ProductManager]}>
      <Helmet title={initialState.productData?.displayName} />
      <AssetManagerPage title={initialState.productData?.displayName as string}>
        <Flex
          sx={{
            flex: 1,
            justifyContent: 'center',
            flexDir: 'column',
            px: 36,
          }}
        >
          {renderUpdateThumbnail()}
          {renderUpdateRefImage()}
          {renderUpdateVersion()}
          {renderQuoteFile()}
          {renderUpdateFile()}
          <ProductSection name="MODEL DETAILS">
            {renderFieldGroups(initialState.productModel, initialState.productData as IProductResource)}
          </ProductSection>
          <Flex justifyContent="center" mb={2}>
            <ButtonProc
              first="EDIT"
              second="PARAMETERS"
              onClick={() => navigate(`${path}/editparameters`)}
              multiline
              size="sm"
            />
          </Flex>
          <ProductSection name="PARAMETERS">
            {renderParameters(initialState.productData?.parameters as IParameter[])}
          </ProductSection>
          <ButtonProc mt={4} first="DELETE" second="PRODUCT" onClick={deleteProductClick} multiline size="sm" />
        </Flex>
      </AssetManagerPage>
    </Layout>
  );
};

export default Product;
